/* Public /campaigns page — a grid of every active + public campaign.
 * Each card links to /campaigns/{slug}. Archived / inactive / private
 * campaigns are filtered server-side and never appear here. */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowRight, Users, Loader2 } from 'lucide-react';
import FloatingDonateTab from '../components/FloatingDonateTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', {
  minimumFractionDigits: 0, maximumFractionDigits: 0,
})}`;

const CampaignCard = ({ c }) => {
  const pct = c.goal > 0 ? Math.min(100, (c.total_raised / c.goal) * 100) : 0;
  const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(c.media_url || '');
  return (
    <Link to={`/campaigns/${c.slug}`}
          data-testid={`campaign-card-${c.slug}`}
          className="group bg-white border border-[#E5E7EB] rounded-lg overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
      <div className="aspect-video bg-[#F3F4F6] relative overflow-hidden">
        {c.media_url ? (
          isVideo ? (
            <video src={c.media_url} className="w-full h-full object-cover"
                   muted playsInline preload="metadata" />
          ) : (
            <img src={c.media_url} alt={c.name} loading="lazy"
                 className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#BA1826]/10 to-[#BA1826]/25">
            <Heart className="w-12 h-12 text-[#BA1826]/40" />
          </div>
        )}
        {c.type && (
          <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-white/90 backdrop-blur px-2 py-0.5 rounded"
                data-testid={`campaign-card-type-${c.slug}`}>
            {c.type.replace(/_/g, ' ')}
          </span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-heading font-bold text-lg text-[#0A0A0A] mb-1 line-clamp-2">{c.name}</h3>
        {(c.country_name || c.location) && (
          <p className="text-xs text-[#6B7280] mb-2">
            {c.country_name}{c.location ? ` — ${c.location}` : ''}
          </p>
        )}
        {c.description && (
          <p className="text-sm text-[#4B5563] mb-4 line-clamp-3 flex-1">{c.description}</p>
        )}
        {c.goal > 0 && (
          <div className="mb-3">
            <div className="relative h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden mb-1">
              <div className="absolute inset-y-0 left-0 bg-[#BA1826] rounded-full"
                   style={{ width: `${pct}%` }}></div>
            </div>
            <p className="text-xs text-[#4B5563]">
              <span className="font-bold text-[#0A0A0A]">{money(c.total_raised)}</span>
              <span className="text-[#9CA3AF]"> raised of </span>
              <span className="font-medium">{money(c.goal)}</span>
              <span className="text-[#9CA3AF]"> · {c.donor_count || 0} donors</span>
            </p>
          </div>
        )}
        <div className="text-[#BA1826] font-medium text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          View campaign <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
};

const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/campaigns`);
        setCampaigns(r.data.campaigns || []);
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const types = useMemo(() => {
    const seen = new Set();
    const out = [];
    campaigns.forEach((c) => {
      if (c.type && !seen.has(c.type)) { seen.add(c.type); out.push(c.type); }
    });
    return out;
  }, [campaigns]);

  const filtered = filterType
    ? campaigns.filter((c) => c.type === filterType)
    : campaigns;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <FloatingDonateTab />
      {/* Hero */}
      <section className="bg-[#0A0A0A] text-white py-14 sm:py-20 px-6 border-b-4 border-[#BA1826]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#BA1826] font-bold mb-3">
            Novena Humanitarian Foundation
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            Active Campaigns
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto">
            Choose a campaign to support directly. Every dollar you give is tracked to
            that specific effort and reported back to you.
          </p>
        </div>
      </section>

      {/* Filters */}
      {types.length > 1 && (
        <section className="border-b border-[#E5E7EB] bg-white">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setFilterType('')}
              data-testid="campaign-filter-all"
              className={`text-xs uppercase tracking-wide font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                !filterType
                  ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                  : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#0A0A0A]'
              }`}>All</button>
            {types.map((t) => (
              <button key={t}
                onClick={() => setFilterType(t)}
                data-testid={`campaign-filter-${t}`}
                className={`text-xs uppercase tracking-wide font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  filterType === t
                    ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                    : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#0A0A0A]'
                }`}>
                {t.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        {loading ? (
          <div className="py-16 text-center" data-testid="campaigns-loading">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#BA1826]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center bg-white border border-dashed border-[#E5E7EB] rounded-lg"
               data-testid="campaigns-empty">
            <Users className="w-10 h-10 mx-auto text-[#9CA3AF] mb-3" />
            <p className="text-[#4B5563]">No active campaigns right now.</p>
            <Link to="/donate" className="btn-primary text-sm inline-flex items-center gap-1.5 mt-4">
              Donate to the general fund <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
               data-testid="campaigns-grid">
            {filtered.map((c) => <CampaignCard key={c.id} c={c} />)}
          </div>
        )}
      </section>
    </div>
  );
};

export default CampaignsPage;
